import { useEffect } from 'react';

import * as Sentry from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

class ErrorLogger {
  isInitialized = false;

  init({
    userId,
    sentryDsn,
  }: {
    userId: string | null;
    sentryDsn: string;
  }): void {
    Sentry.init({
      integrations: [
        Sentry.reactRouterV6BrowserTracingIntegration({
          useEffect,
          useLocation,
          useNavigationType,
          createRoutesFromChildren,
          matchRoutes,
        }),
      ],
      dsn: sentryDsn,
      // Session Replay is deliberately absent: it is billed per recording, the
      // included allowance is small, and this app generated far more than that
      // -- draining the org-wide pay-as-you-go budget that errors are paid
      // from. Nobody on the team watched the recordings. The integration is
      // left out rather than set to 0 so the replay bundle is tree-shaken away.
      //
      // Spans are billed individually and come out of that same shared budget,
      // hence the low trace rate. Sentry's quota is org-wide and spent
      // first-come-first-served, so telemetry nobody reads starves what we do.
      tracesSampleRate: 0.03,
    });
    this.isInitialized = true;
    this.setUser(userId);
  }

  captureException(e: Error, description?: string): void {
    if (this.isInitialized) {
      Sentry.captureException(e);
    }
    if (description) {
      console.error(`${description}: `, e);
    } else {
      console.error(e);
    }
  }

  setUser(userId: string | null): void {
    Sentry.setUser(userId ? { id: userId } : null);
  }
}

const errorLogger = new ErrorLogger();

export default errorLogger;
